<template>
  <div :class="formFieldClasses">
    <fn-label
      v-if="label"
      :id="slotProps.field.name"
      :required="isRequired"
      :class="{ notEmpty: !!slotProps.field.value }"
    >
      <template #default>
        {{ label }}
      </template>
      <template #afterLabel>
        <slot
          name="afterLabel"
          v-bind="slotProps"
        />
      </template>
    </fn-label>

    <fn-form-input
      :valid="isValid"
      :invalid="isInvalid"
      :focused="slotProps.focused"
    >
      <slot
        v-bind="{
          ...slotProps,
          inputAttrs: {
            'aria-invalid': isInvalid ? 'true' : undefined,
            'aria-describedby': hasErrorMessage ? errorId : undefined,
          },
        }"
      />
    </fn-form-input>

    <slot
      v-if="!hideErrors"
      name="errors"
      v-bind="slotProps"
    >
      <fn-error-message
        v-if="hasErrorMessage"
        :id="errorId"
        role="alert"
      >
        {{ errorMessage }}
      </fn-error-message>
    </slot>

    <fn-auto-complete
      v-if="autocompleteStore.getAutoCompleteEnabled"
      :id="id"
      :value="slotProps.field.value as string"
      :focused="slotProps.focused"
      :type="(autoCompleteOptions as FormFieldPropsAutoCompleteOptions)?.type"
    />
  </div>
</template>

<script setup lang="ts">
import type { FormFieldProps, FormFieldPropsAutoCompleteOptions } from '@/mixins/FormField';

type FormFieldComponentProps = FormFieldProps & {
  value?: any;
  option?: boolean;
  hideErrors?: boolean;
};

const props = withDefaults(defineProps<FormFieldComponentProps>(), {
  value: '',
});

const mainStore = useMainStore();
const autocompleteStore = useAutocompleteStore();

/*
 * Prepare FN form field
 *  - Uses ID as name fallback for just-in-case Nuxt2 backwards compatibility
 */
const rules = computed(() => unref(useNormalizedFormRules(props.rules)));
const name = computed(() => props.name || props.id || '');
const isRequired = computed(() => 'required' in unref(rules) && !props.hideRequiredAsterix);

/*
 * Assertions to spot invalid form fields
 *  - Name is a hard required for vee-validate to identify a field
 */
if (!name.value) {
  throw createError({
    statusCode: 500,
    statusMessage: 'Missing prop "name" for "FormField.vue".',
    data: props,
  });
}

/*
 * Prepare vee-validate field
 *  - Syncs VModel value to field value on setup
 *  - Further sync from VModel value to field value happens slotProps.handleChange()
 */
const field = reactive(
  useField(name, rules, {
    type: props.veeType,
    initialValue: props.value || '',
    syncVModel: 'value',
  }),
);

const floatLabel = useFloatLabel(
  () => field.value as MaybeRefOrGetter<any>,
  () => props.floatLabel,
);

const isValid = computed(() => {
  const meta = field.meta;
  const required = unref(rules).required;

  return !!(meta.validated && meta.valid && (required || (!required && !!field.value)));
});

const isInvalid = computed(() => {
  const meta = field.meta;
  const required = unref(rules).required;

  return !!(meta.validated && !meta.valid && (required || (!required && !!field.value)));
});

const formFieldClasses = computed(() => {
  return [
    'form-field',
    unref(floatLabel)?.classes,
    {
      'form-field--option': props.option,
    },
  ];
});

const hasErrorMessage = computed(() => {
  const { meta, errorMessage, errors } = toReactive(field);
  return meta.validated && (errorMessage || errors[0]) && (meta.touched || meta.dirty);
});

const errorMessage = computed(() => {
  const { errorMessage, errors } = toReactive(field);
  return errorMessage || errors[0];
});

const errorId = computed(() => {
  return `form-field-error-${name.value}`;
});

const slotProps = computed(() => ({
  handleChange: (value: any, shouldValidate?: boolean) => {
    field.handleChange(value, shouldValidate);
  },
  handleFocus: () => {
    floatLabel.handleFocus();

    if (props.emitFocus) {
      mainStore.isRootFocus = true;
    }
  },
  handleBlur: (e: Event, shouldValidate?: boolean) => {
    floatLabel.handleBlur();
    field.handleBlur(e, shouldValidate);
  },

  field: reactive(field),
  focused: floatLabel.focused,
}));
</script>

<style lang="scss" scoped>
.form-field {
  position: relative;
  display: block;

  .label {
    margin-bottom: $pad * 0.5;
  }
}

.floatlabel {
  position: relative;

  .label {
    position: absolute;
    padding: 0 4px;
    margin-bottom: 0;
    left: 12px;
    top: 10px;
    z-index: 1;
    background-color: $color-general-background-neutral-default;
    transition: all 0.1s ease-in-out;
    font-family: $font-primary;
    font-weight: normal;
    font-size: map-get($font-sizes, 'l');
    color: palette(black);
    cursor: text;
    max-height: 1em;
  }

  &.disabled {
    .label {
      display: none;
    }
  }

  &:focus-within,
  &.focused,
  &.notEmpty,
  &.populated {
    .label {
      font-size: map-get($font-sizes, 's');
      top: -8px;
      left: $space;
      color: palette(black);
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      width: 90%;
      max-width: fit-content;
      max-height: 1.5em;
    }
  }
}
</style>
